import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Grid,
  Card,
  CardContent,
  Button,
  Checkbox,
  FormControlLabel,
  Divider,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PrintIcon from '@mui/icons-material/Print';
import DownloadIcon from '@mui/icons-material/Download';
import CircularProgress from '@mui/material/CircularProgress';
import { QRCodeSVG } from 'qrcode.react';
import { getAllProducts } from '../services/productService';

/**
 * Admin Print Labels - Genera etichette QR brandizzate stampabili
 */
function AdminPrintLabels() {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const allProducts = await getAllProducts();
      setProducts(allProducts);
    } catch (err) {
      console.error('Error loading products:', err);
    } finally {
      setLoading(false);
    }
  };

  const generateQRUrl = (productId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/try?p=${productId}`;
  };

  const handleSelectProduct = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId)
        ? prev.filter(id => id !== productId)
        : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedProducts([]);
    } else {
      setSelectedProducts(products.map(p => p.id));
    }
    setSelectAll(!selectAll);
  };

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadPDF = () => {
    alert('Funzionalità PDF in arrivo! Per ora usa la stampa (Ctrl/Cmd+P) e seleziona "Salva come PDF"');
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 5 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f5f5f5' }}>
      {/* App Bar - Nascosto in stampa */}
      <AppBar position="static" sx={{ backgroundColor: '#1a1a1a' }} className="no-print">
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={() => navigate('/admin')}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 700 }}>
            Stampa Etichette QR
          </Typography>
          <Button
            variant="outlined"
            color="inherit"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            sx={{ mr: 2 }}
          >
            Download PDF
          </Button>
          <Button
            variant="contained"
            color="success"
            startIcon={<PrintIcon />}
            onClick={handlePrint}
            disabled={selectedProducts.length === 0}
          >
            Stampa
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Instructions - Nascoste in stampa */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="no-print">
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
            📋 Istruzioni
          </Typography>
          <ol>
            <li>Seleziona i prodotti per cui vuoi generare le etichette</li>
            <li>Click su "Stampa" o "Download PDF"</li>
            <li>Stampa su carta adesiva o carta normale</li>
            <li>Taglia lungo le linee tratteggiate</li>
            <li>Applica le etichette vicino ai capi nel negozio</li>
          </ol>
        </Paper>

        {/* Product Selection - Nascosta in stampa */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }} className="no-print">
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Seleziona Prodotti
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectAll}
                  onChange={handleSelectAll}
                />
              }
              label="Seleziona Tutti"
            />
          </Box>

          {products.length === 0 && (
            <Alert severity="info">
              Nessun prodotto disponibile. Aggiungi prodotti dalla dashboard admin.
            </Alert>
          )}

          <Grid container spacing={2}>
            {products.map((product) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id}>
                <Card
                  sx={{
                    border: selectedProducts.includes(product.id) ? '2px solid #4CAF50' : '1px solid #ddd',
                    cursor: 'pointer'
                  }}
                  onClick={() => handleSelectProduct(product.id)}
                >
                  <CardContent>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedProducts.includes(product.id)}
                          onChange={() => handleSelectProduct(product.id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                            {product.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {product.brand} • {product.barcode}
                          </Typography>
                        </Box>
                      }
                    />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          {selectedProducts.length > 0 && (
            <Alert severity="success" sx={{ mt: 2 }}>
              {selectedProducts.length} prodotto{selectedProducts.length > 1 ? 'i' : ''} selezionato{selectedProducts.length > 1 ? 'i' : ''}
            </Alert>
          )}
        </Paper>

        {/* Print Preview - Visibile solo in stampa */}
        <Box className="print-only">
          <style>
            {`
              @media print {
                .no-print {
                  display: none !important;
                }
                .print-only {
                  display: block !important;
                }
                .label-page {
                  page-break-after: always;
                }
                .label-card {
                  page-break-inside: avoid;
                }
              }
              @media screen {
                .print-only {
                  display: none;
                }
              }
            `}
          </style>
        </Box>

        {/* Labels Preview & Print */}
        <Grid container spacing={3}>
          {products
            .filter(p => selectedProducts.includes(p.id))
            .map((product, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={product.id} className="label-card">
                <Paper
                  elevation={3}
                  sx={{
                    p: 3,
                    textAlign: 'center',
                    border: '2px dashed #ccc',
                    borderRadius: '8px',
                    backgroundColor: 'white'
                  }}
                >
                  {/* Logo ProvaMi */}
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 900,
                      letterSpacing: '4px',
                      mb: 1,
                      color: '#1a1a1a'
                    }}
                  >
                    PROVAMI
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  {/* QR Code */}
                  <Box sx={{ my: 2, display: 'flex', justifyContent: 'center' }}>
                    <QRCodeSVG
                      value={generateQRUrl(product.id)}
                      size={180}
                      level="H"
                      includeMargin={true}
                    />
                  </Box>

                  {/* Product Info */}
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 700,
                      fontSize: '14px',
                      mb: 0.5
                    }}
                  >
                    {product.name}
                  </Typography>

                  <Typography
                    variant="body2"
                    sx={{
                      color: '#666',
                      fontSize: '12px',
                      mb: 1
                    }}
                  >
                    {product.brand}
                  </Typography>

                  <Divider sx={{ my: 1 }} />

                  {/* Instructions */}
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: '11px',
                      color: '#666',
                      fontStyle: 'italic'
                    }}
                  >
                    📱 Inquadra con il tuo smartphone
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontSize: '10px',
                      color: '#999',
                      mt: 0.5
                    }}
                  >
                    Prova virtualmente questo capo!
                  </Typography>

                  {/* Barcode (small) */}
                  {product.barcode && (
                    <Typography
                      variant="caption"
                      sx={{
                        display: 'block',
                        fontSize: '9px',
                        color: '#bbb',
                        mt: 1,
                        fontFamily: 'monospace'
                      }}
                    >
                      {product.barcode}
                    </Typography>
                  )}
                </Paper>

                {/* Cut line indicator - solo in anteprima */}
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    textAlign: 'center',
                    mt: 1,
                    color: '#999'
                  }}
                  className="no-print"
                >
                  ✂️ Taglia lungo il bordo tratteggiato
                </Typography>
              </Grid>
            ))}
        </Grid>

        {/* No selection message */}
        {selectedProducts.length === 0 && products.length > 0 && (
          <Paper elevation={2} sx={{ p: 4, textAlign: 'center' }} className="no-print">
            <Typography variant="h6" color="text.secondary">
              Seleziona almeno un prodotto per generare le etichette
            </Typography>
          </Paper>
        )}
      </Container>
    </Box>
  );
}

export default AdminPrintLabels;
